import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogBody } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { requireLogin } from "@/lib/guestGate";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Heart, Clock, CreditCard, Plus } from "lucide-react";
import { brandLabel } from "@/components/MySavedCards";
import { CURRENCIES, autoCurrency, suggestedAmounts } from "@/lib/currencies";
import { celebrateDonationSent, donorThanksBody } from "@/lib/donationEvents";
import { donationsAvailable, fmtMoney } from "@/lib/payments";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";

// Cache one Stripe.js instance per publishable key.
const _stripeCache = {};
const getStripe = (pk) => { if (pk && !_stripeCache[pk]) _stripeCache[pk] = loadStripe(pk); return pk ? _stripeCache[pk] : null; };

// Sélecteur compact (une ligne) dessiné par Yalola : carte(s) mémorisée(s) chez Stripe + « Nouvelle carte ».
// Les onglets « Enregistré / Carte bancaire » du Payment Element ne sont jamais affichés.
const CardChoice = ({ cards, selected, onSelect, disabled, t }) => {
  const cls = (on) => `pill shrink-0 gap-1.5 !px-3 !py-1.5 text-xs ${on ? "pill-primary" : "pill-outline"}`;
  return (
    <div className="flex flex-wrap gap-2" data-testid="donate-card-choice">
      {cards.map((c) => (
        <button key={c.id} type="button" disabled={disabled} onClick={() => onSelect(c.id)} className={cls(selected === c.id)} data-testid={`donate-saved-card-${c.last4}`}>
          <CreditCard size={14} /> {brandLabel(c.brand)} •••• {c.last4}
        </button>
      ))}
      <button type="button" disabled={disabled} onClick={() => onSelect("new")} className={cls(selected === "new")} data-testid="donate-new-card">
        <Plus size={14} /> {t("donate.new_card", "Nouvelle carte")}
      </button>
    </div>
  );
};

// Étape carte (Stripe Payment Element) — corps qui défile + pied FIXE (Retour / Payer, Safe Area). Carte mémorisée
// présélectionnée → formulaire masqué, un tap sur Payer (confirmCardPayment) ; « Nouvelle carte » → formulaire + case.
const EmbeddedPayStep = ({ quote, receiverName, cur, lang, amountLabel, onPaid, onBack, t, piId, clientSecret, canSave = false, savedCards = [] }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState("");
  // Carte mémorisée : NOTRE case, cochée par défaut (Stripe ne permet pas de pré-cocher la sienne — elle est donc
  // masquée côté serveur). Décochée → l'intention perd setup_future_usage et la carte n'est pas réaffichée.
  const [saveCard, setSaveCard] = useState(true);
  const [choice, setChoice] = useState(() => savedCards[0]?.id || "new"); // pm_… | "new"
  const syncedSave = useRef(true); // dernier choix transmis au serveur (l'intention naît avec on_session)
  const usingSaved = choice !== "new";
  const canPay = !!stripe && !paying && (usingSaved || (!!elements && ready));
  const choose = (v) => { setChoice(v); setReady(false); setErr(""); };
  const pay = async () => {
    if (!canPay) return;
    setPaying(true); setErr("");
    try {
      let res;
      if (usingSaved) {
        res = await stripe.confirmCardPayment(clientSecret, { payment_method: choice });
      } else {
        if (syncedSave.current !== saveCard) {
          await api.post(`/payments/donation/intent/${piId}/save-card`, { save: saveCard });
          syncedSave.current = saveCard;
        }
        res = await stripe.confirmPayment({ elements, redirect: "if_required", confirmParams: { payment_method_data: { allow_redisplay: saveCard ? "always" : "limited" } } });
      }
      const { error, paymentIntent } = res || {};
      if (error) { setErr(error.message || t("common.error", "Erreur")); setPaying(false); return; }
      if (paymentIntent && (paymentIntent.status === "succeeded" || paymentIntent.status === "processing")) {
        await onPaid();
      } else { setErr(t("donate.pay_incomplete", "Paiement non finalisé, réessaie.")); setPaying(false); }
    } catch (e) {
      setErr(e?.friendlyMessage || e?.message || t("common.error", "Erreur")); setPaying(false);
    }
  };
  const label = paying ? t("off.processing", "Traitement...") : (!usingSaved && !ready ? t("donate.pay_loading_short", "Chargement…") : amountLabel);
  return (<>
    <DialogBody className="space-y-3" data-testid="donate-embedded-form">
      <QuoteBreakdown quote={quote} receiverName={receiverName} cur={cur} lang={lang} t={t} />
      {savedCards.length > 0 && <CardChoice cards={savedCards} selected={choice} onSelect={choose} disabled={paying} t={t} />}
      {usingSaved ? (
        <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="donate-saved-hint">
          {t("donate.saved_card_hint", "Un seul tap : ta carte mémorisée est débitée. Gérable dans Paramètres › Mes cartes.")}
        </p>
      ) : (<>
        <PaymentElement
          options={{ layout: "tabs", terms: { card: "never" } }}
          onReady={() => setReady(true)}
          onLoadError={() => setErr(t("donate.pay_load_error", "Impossible de charger le paiement. Réessaie dans un instant."))}
        />
        {ready && canSave && (
          <label className="flex items-start gap-2 text-xs cursor-pointer select-none" data-testid="donate-save-card">
            <input type="checkbox" className="mt-0.5 h-4 w-4 rounded accent-[var(--yalola-primary)]" checked={saveCard}
                   onChange={(e) => setSaveCard(e.target.checked)} data-testid="donate-save-card-checkbox" />
            <span>
              <span className="font-medium">{t("donate.save_card", "Enregistrer ma carte pour la prochaine fois")}</span>
              <span className="block" style={{ color: "var(--yalola-text-muted)" }}>{t("donate.save_card_note", "Un seul tap au prochain soutien · gardée par Stripe, jamais par Yalola · retirable à tout moment")}</span>
            </span>
          </label>
        )}
        {!ready && !err && (
          <div className="flex items-center gap-2 text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="donate-pay-loading">
            <span className="h-4 w-4 rounded-full border-2 animate-spin" style={{ borderColor: "var(--yalola-primary)", borderTopColor: "transparent" }} />
            {t("donate.pay_loading", "Chargement du paiement sécurisé…")}
          </div>
        )}
      </>)}
      {err && <p className="text-xs" style={{ color: "#EF4444" }} data-testid="donate-pay-error">{err}</p>}
    </DialogBody>
    <DialogFooter className="flex-row">
      <Button variant="outline" onClick={onBack} disabled={paying} data-testid="donate-pay-back">{t("common.back", "Retour")}</Button>
      <Button className="pill-primary flex-1" onClick={pay} disabled={!canPay} data-testid="donate-pay-submit">{label}</Button>
    </DialogFooter>
  </>);
};

// Récapitulatif : le SOUTIEN en évidence ; le total débité et les frais Stripe (tarif réel) sur une ligne discrète.
const QuoteBreakdown = ({ quote, receiverName, cur, lang, t }) => {
  if (!quote) return null;
  return (
    <div className="rounded-xl border p-3 space-y-1.5 text-sm" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }} data-testid="donate-quote">
      <div className="flex justify-between gap-3 font-bold"><span>{t("donate.q_support", "Soutien à {name}").replace("{name}", receiverName)}</span><b data-testid="donate-quote-amount">{fmtMoney(quote.amount, cur, lang)}</b></div>
      {quote.fee > 0 && (
        <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="donate-quote-total-line">
          {t("donate.q_total_line", "Total débité {total} · dont {fee} de frais Stripe").replace("{total}", fmtMoney(quote.total, cur, lang)).replace("{fee}", fmtMoney(quote.fee, cur, lang))}
        </p>
      )}
      <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid="donate-quote-note">
        {quote.commission_percent > 0
          ? t("donate.q_note_commission", "Yalola prélève une commission de {pct} % sur ton soutien : {name} reçoit {net}. Les frais couvrent le prestataire de paiement.")
              .replace("{pct}", String(quote.commission_percent)).replace("{name}", receiverName)
              .replace("{net}", fmtMoney(Math.round(quote.amount * (100 - quote.commission_percent)) / 100, cur, lang))
          : t("donate.q_note", "100 % de ton soutien est versé à {name}. Les frais couvrent uniquement le prestataire de paiement — Yalola ne perçoit rien.").replace("{name}", receiverName)}
      </p>
    </div>
  );
};

export const DonateModal = ({ open, onOpenChange, receiver, liveId }) => {
  const { user, t, config, lang } = useApp();
  const cur = autoCurrency({ userCountry: user?.country, preferredCurrency: user?.preferred_currency, defaultCurrency: config?.settings?.default_currency });
  const sym = (CURRENCIES.find((c) => c.code === cur) || {}).symbol || cur;
  const presets = suggestedAmounts(cur);
  const [amount, setAmount] = useState(() => suggestedAmounts(cur)[0]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [step, setStep] = useState("form"); // form | pay
  const [clientSecret, setClientSecret] = useState(null);
  const [pubKey, setPubKey] = useState(null);
  const [piId, setPiId] = useState(null);
  const [customerSession, setCustomerSession] = useState(null); // client Stripe présent → case « Enregistrer ma carte »
  const [savedCards, setSavedCards] = useState([]); // cartes mémorisées (Paramètres › Mes cartes) → sélecteur compact
  const settledRef = useRef(false); // paiement confirmé / envoyé → ne jamais annuler l'intention à la fermeture
  const available = donationsAvailable(config?.payments, config?.settings?.features);

  useEffect(() => { setAmount(suggestedAmounts(cur)[0]); }, [cur]);
  useEffect(() => {
    if (!open || !user || !available) return;
    api.get("/payments/cards").then(({ data }) => setSavedCards(data?.cards || [])).catch(() => setSavedCards([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.id, available]);
  // Popup fermé à l'étape carte sans payer → l'intention encore ouverte est annulée chez Stripe (ligne « Annulé »,
  // jamais « En attente »). Après un paiement confirmé / envoyé, rien n'est touché.
  useEffect(() => {
    if (open) return;
    if (step === "pay" && piId && !settledRef.current) api.post(`/payments/checkout/${piId}/cancel`).catch(() => {});
    // Remise à zéro après l'animation de fermeture (sinon le formulaire réapparaît un instant pendant le fondu).
    const tm = setTimeout(() => { setStep("form"); setClientSecret(null); setCustomerSession(null); setPiId(null); setLoading(false); settledRef.current = false; }, 260);
    return () => clearTimeout(tm);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Live quote (fee grid is configured in Admin › Finance and may depend on currency / country).
  useEffect(() => {
    if (!open || !available) return undefined;
    const a = Number(amount);
    if (!a || a <= 0) { setQuote(null); return undefined; }
    const id = setTimeout(() => {
      api.get(`/payments/donation/quote?amount=${a}&currency=${cur}`).then(({ data }) => setQuote(data)).catch(() => setQuote(null));
    }, 300);
    return () => clearTimeout(id);
  }, [open, amount, cur, available]);

  const totalLabel = quote ? fmtMoney(quote.total, cur, lang) : `${amount} ${sym}`;
  const sheetPct = Math.max(50, Math.min(Number(config?.settings?.donation_sheet_height_pct) || 80, 100)); // Admin › Paramètres › Soutiens
  const supportLabel = quote ? fmtMoney(quote.amount, cur, lang) : `${amount} ${sym}`;

  // Popup de paiement intégré (Stripe Payment Element, carte) — MÊME parcours sur le site web ET dans l'app
  // (Android/iOS) : on ne quitte ni le profil ni le direct. Seuls les moyens réglés SUR PLACE sont proposés
  // (allow_redirects: never côté serveur) → jamais Bancontact/iDEAL dans ce popup ; 3-D Secure s'affiche dans un
  // cadre à l'intérieur du popup. Un seul système partout : plus de Stripe Checkout ni de Bancontact.
  const startEmbedded = async () => {
    if (!user) { requireLogin("donate"); return; }
    if (!amount || amount <= 0) { toast.error(t("donate.amount_required", "Montant requis")); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/donation/intent", {
        receiver_id: receiver.id, amount: Number(amount), currency: cur, message, live_id: liveId || undefined,
      });
      setPubKey(data.publishable_key);
      setClientSecret(data.client_secret);
      setCustomerSession(data.customer_session_client_secret || null);
      setPiId(data.payment_intent_id);
      settledRef.current = false;
      if (data.quote) setQuote((q) => ({ ...(q || {}), ...data.quote }));
      setStep("pay");
      setLoading(false);
    } catch (e) {
      setLoading(false);
      toast.error(e?.friendlyMessage || t("common.error", "Erreur"));
    }
  };

  // Carte confirmée par Stripe dans le popup → le serveur relit l'intention et crédite (même chemin que le webhook),
  // puis fête SUR PLACE (cœurs + « Merci ! Ton don de 1 € a été reçu » + phrase du bénéficiaire). Confirmation en
  // retard (« processing ») → petit mot d'attente ; la notification « Merci » suivra.
  const onEmbeddedPaid = async () => {
    let paid = false;
    try { const { data } = await api.get(`/payments/intent-status/${piId}`); paid = data?.payment_status === "paid"; } catch { /* noop */ }
    settledRef.current = true;
    onOpenChange(false);
    if (paid) celebrateDonationSent(Number(quote?.amount) || Number(amount) || 0, cur, piId, donorThanksBody(receiver, t));
    else toast.success(t("donate.pending_confirm", "Paiement envoyé — la confirmation arrive dans un instant 🙏"));
  };

  if (!receiver) return null;
  const verb = t("donate.verb", "Soutenir");
  const confirm = () => startEmbedded();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="donate-modal" variant="bottom" className="z-[95]" overlayClassName="bg-black/50" style={{ "--yalola-bottom-sheet-h": `${sheetPct}dvh` }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart size={18} style={{ color: "var(--yalola-primary)" }} /> {verb} {receiver.name}
          </DialogTitle>
        </DialogHeader>
        {!available ? (
          <DialogBody>
            <div className="rounded-xl p-4 text-sm flex items-start gap-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid="donate-unavailable">
              <Clock size={16} className="mt-0.5 shrink-0" style={{ color: "var(--yalola-text-muted)" }} />
              <span>{t("donate.ios_soon", "Le soutien n'est pas encore disponible dans l'application iOS. Il arrive bientôt.")}</span>
            </div>
          </DialogBody>
        ) : step === "pay" && clientSecret && pubKey ? (
          // Pas de customerSessionClientSecret : les cartes mémorisées sont proposées par NOTRE sélecteur compact,
          // jamais par les onglets du Payment Element. La sauvegarde reste pilotée par setup_future_usage + allow_redisplay.
          <Elements stripe={getStripe(pubKey)} options={{ clientSecret, locale: lang || "auto", appearance: { theme: "stripe", variables: { colorPrimary: "#2D5A4C" } } }}>
            <EmbeddedPayStep quote={quote} receiverName={receiver.name} cur={cur} lang={lang} t={t} piId={piId} clientSecret={clientSecret}
              canSave={!!customerSession} savedCards={savedCards} amountLabel={`${t("donate.pay_btn", "Payer")} ${totalLabel}`} onPaid={onEmbeddedPaid}
              onBack={() => { if (piId) api.post(`/payments/checkout/${piId}/cancel`).catch(() => {}); setPiId(null); setClientSecret(null); setCustomerSession(null); setStep("form"); }} />
          </Elements>
        ) : (<>
        <DialogBody className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {presets.map(a => (
              <button
                key={a}
                onClick={() => setAmount(a)}
                className={`pill ${Number(amount) === a ? "pill-primary" : "pill-outline"}`}
                data-testid={`donate-preset-${a}`}
              >{a.toLocaleString()} {sym}</button>
            ))}
          </div>
          <div>
            <label className="text-xs font-medium">{t("donate.amount", "Montant du soutien")} ({sym} {cur})</label>
            <input
              type="number"
              min="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 w-full h-11 rounded-xl px-3 border bg-white"
              style={{ borderColor: "var(--yalola-border)" }}
              data-testid="donate-amount-input"
            />
          </div>
          <QuoteBreakdown quote={quote} receiverName={receiver.name} cur={cur} lang={lang} t={t} />
          <div>
            <label className="text-xs font-medium">{t("donate.message_opt", "Message (optionnel)")}</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-xl p-3 border bg-white"
              style={{ borderColor: "var(--yalola-border)" }}
              placeholder={t("donate.msg_placeholder", "Un mot d'encouragement...")}
              data-testid="donate-message-input"
            />
          </div>
          <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
            {t("donate.secure_note", "Paiement sécurisé par Stripe, selon les modalités affichées au moment du paiement.")}
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="donate-cancel">{t("common.cancel", "Annuler")}</Button>
          <Button className="pill-primary" onClick={confirm} disabled={loading} data-testid="donate-confirm">
            {loading ? t("off.processing", "Traitement...") : `${verb} · ${supportLabel}`}
          </Button>
        </DialogFooter>
        </>)}
      </DialogContent>
    </Dialog>
  );
};
